import { useRef, useEffect, useCallback } from 'react';

import {
  fetchUploadLink,
  uploadVideo,
  moveVimeoVideo,
  getVideoIdFromURI,
} from '../../../../utils/vimeo';
import { UPLOAD_CONCURRENCY, UploadStatus, UploadStep } from './constants';

// Exponential-moving-average weight for the upload rate — smooths out the
// jittery instantaneous rates of individual progress events so the displayed
// time-remaining doesn't jump around.
const RATE_SMOOTHING = 0.2;

/**
 * Bounded-concurrency upload queue for the bulk exercise creator.
 *
 * Cards enter the queue and upload UPLOAD_CONCURRENCY at a time; the rest wait
 * in QUEUED and start automatically as slots free up. Each job runs the three
 * upload steps (generateVimeoUpload → byte upload → moveVimeoVideo) and reports
 * per-card state through `onUpdate(cardId, uploadPatch)`.
 *
 * Retry semantics: a `videoId` obtained from a completed byte upload is never
 * discarded — a MOVE failure retries only the move, while CREATE/UPLOAD
 * failures restart from a fresh upload link.
 *
 * The hook owns no card state; the parent remains the single source of truth.
 */
const useVideoUploadQueue = ({
  coachId,
  coachName,
  remote,
  onUpdate,
  onUploaded,
}) => {
  const queueRef = useRef([]); // pending jobs: { cardId, file, videoId }
  const activeCountRef = useRef(0);
  const abortHandlesRef = useRef(new Map()); // cardId -> abort function for the in-flight XHR
  const cancelledRef = useRef(new Set()); // cardIds removed while queued/uploading
  const lastEmitRef = useRef(new Map()); // cardId -> last emitted "progress|eta" pair
  const rateTrackerRef = useRef(new Map()); // cardId -> { lastLoaded, lastTime, rate } for ETA estimates

  // Keep latest callbacks/identifiers available to long-running jobs without
  // re-creating the queue when they change.
  const callbacksRef = useRef({});
  callbacksRef.current = {
    coachId, coachName, remote, onUpdate, onUploaded,
  };

  const isCancelled = useCallback((cardId) => cancelledRef.current.has(cardId), []);

  const emitUpdate = useCallback((cardId, patch) => {
    if (!isCancelled(cardId)) {
      callbacksRef.current.onUpdate(cardId, patch);
    }
  }, [isCancelled]);

  // Stable: reads everything through refs, so it never goes stale.
  const runJob = useCallback(async (job) => {
    const { cardId, file } = job;
    const { coachId: coach, coachName: name, remote: remoteFn } = callbacksRef.current;
    let { videoId } = job;

    lastEmitRef.current.set(cardId, '');
    rateTrackerRef.current.set(cardId, { lastLoaded: 0, lastTime: Date.now(), rate: 0 });
    emitUpdate(cardId, {
      status: UploadStatus.UPLOADING,
      // A retained videoId means the bytes already reached Vimeo (move-only
      // retry) — show the bar full instead of restarting from 0.
      progress: videoId ? 100 : 0,
      etaSeconds: null,
      error: null,
      failedStep: null,
      videoId,
    });

    try {
      // A retained videoId means the bytes are already on Vimeo and only the
      // move failed — skip straight to Step 3.
      if (!videoId) {
        // Step 1 — create the upload slot.
        let uploadLink;
        try {
          const videoName = `${name.replaceAll(' ', '-')}_${file.name}`;
          const linkData = await fetchUploadLink(file.size, videoName, coach, remoteFn);
          uploadLink = linkData.uploadLink;
          videoId = getVideoIdFromURI(linkData.videoURI);
        } catch (error) {
          error.failedStep = UploadStep.CREATE;
          throw error;
        }

        if (isCancelled(cardId)) {
          return;
        }

        // Step 2 — byte upload with progress, abortable if the card is removed.
        try {
          await uploadVideo(uploadLink, file, {
            onProgress: (progress, { loaded, total }) => {
              // Estimate time remaining from a smoothed upload rate — raw
              // per-event rates are too jittery to display directly.
              const tracker = rateTrackerRef.current.get(cardId);
              let etaSeconds = null;
              if (tracker) {
                const now = Date.now();
                const elapsedMs = now - tracker.lastTime;
                if (elapsedMs > 0 && loaded > tracker.lastLoaded) {
                  const instantRate = ((loaded - tracker.lastLoaded) / elapsedMs) * 1000; // bytes/sec
                  tracker.rate = tracker.rate
                    ? RATE_SMOOTHING * instantRate + (1 - RATE_SMOOTHING) * tracker.rate
                    : instantRate;
                  tracker.lastLoaded = loaded;
                  tracker.lastTime = now;
                }
                if (tracker.rate > 0) {
                  etaSeconds = Math.max(0, Math.round((total - loaded) / tracker.rate));
                }
              }
              // Throttle to visible changes (integer percent or whole-second
              // ETA) so 50 concurrent uploads don't flood the card list with
              // re-renders.
              const emitKey = `${progress}|${etaSeconds}`;
              if (lastEmitRef.current.get(cardId) !== emitKey) {
                lastEmitRef.current.set(cardId, emitKey);
                emitUpdate(cardId, { status: UploadStatus.UPLOADING, progress, etaSeconds });
              }
            },
            onAbortHandle: (abort) => abortHandlesRef.current.set(cardId, abort),
          });
        } catch (error) {
          error.failedStep = UploadStep.UPLOAD;
          throw error;
        } finally {
          abortHandlesRef.current.delete(cardId);
        }
      }

      if (isCancelled(cardId)) {
        return;
      }

      // Step 3 — move the video into the destination folder.
      try {
        await moveVimeoVideo(videoId, coach, remoteFn);
      } catch (error) {
        error.failedStep = UploadStep.MOVE;
        throw error;
      }

      emitUpdate(cardId, {
        status: UploadStatus.UPLOADED, progress: 100, etaSeconds: null, error: null, failedStep: null, videoId,
      });
      if (!isCancelled(cardId)) {
        callbacksRef.current.onUploaded(cardId, videoId);
      }
    } catch (error) {
      emitUpdate(cardId, {
        status: UploadStatus.FAILED,
        etaSeconds: null,
        error: error.message,
        failedStep: error.failedStep || null,
        // Keep the videoId on a move failure so retry skips the byte upload.
        videoId: error.failedStep === UploadStep.MOVE ? videoId : null,
      });
    } finally {
      rateTrackerRef.current.delete(cardId);
      lastEmitRef.current.delete(cardId);
    }
  }, [isCancelled, emitUpdate]);

  const pump = useCallback(() => {
    while (activeCountRef.current < UPLOAD_CONCURRENCY && queueRef.current.length > 0) {
      const job = queueRef.current.shift();
      if (isCancelled(job.cardId)) {
        cancelledRef.current.delete(job.cardId);
        // eslint-disable-next-line no-continue
        continue;
      }
      activeCountRef.current += 1;
      runJob(job).finally(() => {
        activeCountRef.current -= 1;
        pump();
      });
    }
  }, [runJob, isCancelled]);

  const enqueue = useCallback((cardId, file, { videoId = null } = {}) => {
    cancelledRef.current.delete(cardId);
    queueRef.current.push({ cardId, file, videoId });
    pump();
  }, [pump]);

  // Cancels a card's upload: aborts the in-flight request or drops it from the
  // queue. Used when a card is removed or its file is replaced.
  const cancel = useCallback((cardId) => {
    cancelledRef.current.add(cardId);
    const abort = abortHandlesRef.current.get(cardId);
    if (abort) {
      abort();
      abortHandlesRef.current.delete(cardId);
    }
  }, []);

  // Abort everything on unmount so orphaned requests don't keep uploading.
  useEffect(() => () => {
    abortHandlesRef.current.forEach((abort) => abort());
    abortHandlesRef.current.clear();
    queueRef.current = [];
  }, []);

  return { enqueue, cancel };
};

export default useVideoUploadQueue;
